import { Info } from '@phosphor-icons/react';
import { Button, Stack } from '@fragments-sdk/ui';
import { WorkspacePageHeader } from '@/components/layout';
import type {
  DashboardDrillTarget,
  GovernanceCiStatus,
} from './dashboard-brief';
import {
  DEFAULT_AUTOMATION_GATE,
  SetupSteps,
  getRecommendedStep,
} from './dashboard-setup-steps';
import { RecommendedActionCard, Scoreboard } from './dashboard-scoreboard';
import { AreaRiskBand } from './dashboard-area-risk';
import { DashboardActivity } from './dashboard-activity';
import s from './dashboard.module.scss';
import './dashboard-preview-global.scss';

export type DashboardMode = 'live' | 'preview';

export type FeedStatus = 'pass' | 'fail' | 'warn' | 'info';

export interface DashboardFeedItem {
  title: string;
  meta: string;
  ago: string;
  status: FeedStatus;
}

// Product-area criticality — a label, never chroma. Mirrors the literal set in
// `convex/schema/domains/findings` and `findings-types.ts`.
export type AreaCriticality =
  | 'low'
  | 'medium'
  | 'high'
  | 'revenue'
  | 'regulated';

export interface AreaRiskRow {
  areaId: string;
  areaName: string;
  criticality: AreaCriticality | null;
  open: number;
  new: number;
}

export interface DashboardMetrics {
  enrichmentScore: {
    score: number;
    grade: string;
    enrichedCount: number;
    totalComponents: number;
    needsGuidance: number;
    weekDelta: number;
  };
  componentCoverage: {
    total: number;
    core: number;
    composition: number;
    unclassified: number;
    newlyGovernedThisWeek: number;
  };
  tokenDiscovery: {
    count: number;
    categories: string[];
    monthDelta: number;
  };
  recentActivity: {
    thisWeekCount: number;
    sparkline: number[];
    lastEventAt: number | null;
  };
  connectedRepos: number;
  findings: {
    open: number;
    byRule: Record<string, number>;
    trend: number[];
    resolvedThisWeek: number;
  };
  repoPosture: {
    open: number;
    byRule: Record<string, number>;
    trend: number[];
    resolvedThisWeek: number;
  };
  prDrift: {
    open: number;
    byRule: Record<string, number>;
    trend: number[];
    resolvedThisWeek: number;
  };
  decisionIntelligence: {
    total: number;
    token: number;
    component: number;
    safeFixes: number;
    needsReview: number;
    policyNeeded: number;
    topKind: string | null;
    topTitle: string | null;
  };
  driftCaught: {
    thisPeriod: number;
    priorPeriod: number;
    percentChange: number;
  };
  driftTrend: number[];
  driftShipped: {
    thisPeriod: number;
    priorPeriod: number;
    percentChange: number;
    weeklyTrend: number[];
  };
  myPrFindings: {
    open: number;
    autofixable: number;
  };
  areaRisk: AreaRiskRow[];
  recommendedFixScenario: DashboardFixScenario | null;
  scannerEvidence?: DashboardScannerEvidence;
  feed: DashboardFeedItem[];
}

export interface DashboardScannerEvidence {
  available: boolean;
  repoFullName: string | null;
  branch: string | null;
  commitSha: string | null;
  scannedAt: number | null;
  components: Array<{
    factId: string;
    name?: string;
    filePath?: string;
    props: string[];
    packageName?: string;
    importPath?: string;
  }>;
  tokens: Array<{
    factId: string;
    name?: string;
    filePath?: string;
    line?: number;
    category?: string;
    value?: string;
    tier?: string;
  }>;
  diagnostics: Array<{
    factId: string;
    filePath?: string;
    category?: string;
    severity?: string;
    message?: string;
  }>;
  componentAdoption: Array<{
    factId: string;
    name?: string;
    filePath?: string;
    confidence?: string;
    totalImporters?: number;
    totalCallSites?: number;
    topImporters: string[];
  }>;
  tokenAdoption: Array<{
    factId: string;
    name?: string;
    category?: string;
    value?: string;
    confidence?: string;
    directReferences?: number;
    valueReferences?: number;
    uniqueFiles?: number;
    topReferences: string[];
  }>;
}

export interface DashboardFixScenario {
  kind: 'autofix_file' | 'autofix_decision';
  repoFullName: string;
  filePath: string;
  findingIds: string[];
  findingCount: number;
  fixableCount: number;
  ruleIds: string[];
  decisionTitle?: string;
}

export type DashboardFixScenarioStatus =
  | 'idle'
  | 'running'
  | 'success'
  | 'error';

export interface DashboardFixScenarioState {
  status: DashboardFixScenarioStatus;
  prUrl?: string;
  prNumber?: number;
  fixedCount?: number;
  error?: string;
}

export interface DashboardAutomationGate {
  enabled: boolean;
  currentPlanLabel: string;
  requiredPlanLabel: string;
}

export const EMPTY_DASHBOARD_METRICS: DashboardMetrics = {
  enrichmentScore: {
    score: 0,
    grade: 'D',
    enrichedCount: 0,
    totalComponents: 0,
    needsGuidance: 0,
    weekDelta: 0,
  },
  componentCoverage: {
    total: 0,
    core: 0,
    composition: 0,
    unclassified: 0,
    newlyGovernedThisWeek: 0,
  },
  tokenDiscovery: {
    count: 0,
    categories: [],
    monthDelta: 0,
  },
  recentActivity: {
    thisWeekCount: 0,
    sparkline: [],
    lastEventAt: null,
  },
  connectedRepos: 0,
  findings: {
    open: 0,
    byRule: {},
    trend: [],
    resolvedThisWeek: 0,
  },
  repoPosture: {
    open: 0,
    byRule: {},
    trend: [],
    resolvedThisWeek: 0,
  },
  prDrift: {
    open: 0,
    byRule: {},
    trend: [],
    resolvedThisWeek: 0,
  },
  decisionIntelligence: {
    total: 0,
    token: 0,
    component: 0,
    safeFixes: 0,
    needsReview: 0,
    policyNeeded: 0,
    topKind: null,
    topTitle: null,
  },
  driftCaught: {
    thisPeriod: 0,
    priorPeriod: 0,
    percentChange: 0,
  },
  driftTrend: [],
  driftShipped: {
    thisPeriod: 0,
    priorPeriod: 0,
    percentChange: 0,
    weeklyTrend: [],
  },
  myPrFindings: {
    open: 0,
    autofixable: 0,
  },
  areaRisk: [],
  recommendedFixScenario: null,
  scannerEvidence: {
    available: false,
    repoFullName: null,
    branch: null,
    commitSha: null,
    scannedAt: null,
    components: [],
    tokens: [],
    diagnostics: [],
    componentAdoption: [],
    tokenAdoption: [],
  },
  feed: [],
};

export interface DashboardSurfaceProps {
  mode: DashboardMode;
  metrics: DashboardMetrics;
  ciStatus?: GovernanceCiStatus;
  onNavigate?: (target: DashboardDrillTarget) => void;
  onCreateFixScenario?: (scenario: DashboardFixScenario) => void;
  fixScenarioState?: DashboardFixScenarioState;
  automationGate?: DashboardAutomationGate;
  showTourReplay?: boolean;
  onRestartTour?: () => void;
}

// The dashboard runs in two lifecycle phases that never blend:
//   • Setup — no governance in CI yet. Show the guided checklist; component
//     and token counts live on those cards (and nowhere else).
//   • Operational — governance is wired into CI. Show outcome KPIs (drift
//     caught vs shipped, decisions, readiness), the single recommended action,
//     and recent activity. Inventory counts are intentionally dropped here.
export function DashboardSurface({
  metrics,
  ciStatus,
  onNavigate,
  onCreateFixScenario,
  fixScenarioState,
  automationGate = DEFAULT_AUTOMATION_GATE,
  showTourReplay = false,
  onRestartTour,
}: DashboardSurfaceProps) {
  const governanceConfigured =
    ciStatus === 'active' || ciStatus === 'broken' || ciStatus === 'degraded';

  const recommendedStep = getRecommendedStep({
    findingsOpen: metrics.findings.open,
    decisions: metrics.decisionIntelligence,
    componentCount: metrics.componentCoverage.total,
    tokenCount: metrics.tokenDiscovery.count,
    fixScenario: metrics.recommendedFixScenario,
    fixScenarioState,
    myPrFindings: metrics.myPrFindings,
    governanceConfigured,
    automationGate,
    onCreateFixScenario,
  });

  return (
    <>
      <PageTitle
        showTourReplay={showTourReplay}
        onRestartTour={onRestartTour}
      />

      <Stack className={s.dashboardBody} gap="md">
        {governanceConfigured ? (
          <>
            <Scoreboard metrics={metrics} onNavigate={onNavigate} />
            <RecommendedActionCard
              step={recommendedStep}
              onNavigate={onNavigate}
            />
            <AreaRiskBand metrics={metrics} onNavigate={onNavigate} />
            <DashboardActivity metrics={metrics} onNavigate={onNavigate} />
          </>
        ) : (
          <SetupSteps
            findingsOpen={metrics.findings.open}
            decisions={metrics.decisionIntelligence}
            componentCount={metrics.componentCoverage.total}
            tokenCount={metrics.tokenDiscovery.count}
            unclassified={metrics.componentCoverage.unclassified}
            scannerEvidence={metrics.scannerEvidence}
            myPrFindings={metrics.myPrFindings}
            fixScenario={metrics.recommendedFixScenario}
            fixScenarioState={fixScenarioState}
            automationGate={automationGate}
            onNavigate={onNavigate}
            onCreateFixScenario={onCreateFixScenario}
          />
        )}
      </Stack>
    </>
  );
}

export function DashboardSurfaceSkeleton({
  showTourReplay = false,
  onRestartTour,
}: {
  showTourReplay?: boolean;
  onRestartTour?: () => void;
}) {
  return (
    <>
      <PageTitle
        showTourReplay={showTourReplay}
        onRestartTour={onRestartTour}
      />

      <div
        className={s.dashboardBody}
        data-loading="true"
        aria-label="Loading dashboard"
        aria-busy="true"
      >
        <div className={s.statGrid} data-tour="stat-grid">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className={`${s.statTile} ${s.statTileSkeleton}`}>
              <span className={s.skeletonLine} data-size="label" />
              <span className={s.skeletonLine} data-size="value" />
              <span className={s.skeletonLine} data-size="meta" />
            </div>
          ))}
        </div>

        <ul className="fs-cdp-setupList" aria-hidden="true">
          {[
            'setup-recommended',
            'setup-components',
            'setup-tokens',
            'setup-governance',
          ].map((tourTarget, index) => (
            <li
              key={tourTarget}
              className={`fs-cdp-setupCard ${s.setupRowSkeleton}`}
              data-setup-card
              data-tour={tourTarget}
              data-tone="accent"
              data-has-illustration
              style={{ animationDelay: `${0.16 + index * 0.04}s` }}
            >
              <span className={`fs-cdp-setupIllustration ${s.skeletonMedia}`} />
              <span className={s.skeletonLine} data-size="title" />
              <span className={s.skeletonLine} data-size="body" />
              <span className={s.skeletonButton} />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

// ── Header ───────────────────────────────────────────────────────

function PageTitle({
  showTourReplay = false,
  onRestartTour,
}: {
  showTourReplay?: boolean;
  onRestartTour?: () => void;
}) {
  return (
    <WorkspacePageHeader
      title="Dashboard"
      actions={
        showTourReplay ? (
          <Button
            type="button"
            variant="outlined"
            size="xs"
            onClick={onRestartTour}
          >
            <Info size={14} />
            Take a tour
          </Button>
        ) : null
      }
    />
  );
}
